
import { Link } from 'react-router-dom';

export default function DisasterMedicationManagementPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center">
                <i className="ri-capsule-fill text-white text-xl"></i>
              </div>
              <span className="text-2xl font-bold text-gray-900" style={{ fontFamily: '"Pacifico", serif' }}>PharmaBeast</span>
            </Link>
            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/" className="text-gray-600 hover:text-teal-600 transition-colors">ホーム</Link>
              <Link to="/services" className="text-gray-600 hover:text-teal-600 transition-colors">サービス</Link>
              <Link to="/beast-blog" className="text-teal-600 font-medium">ビースト君ブログ</Link>
              <Link to="/contact" className="text-gray-600 hover:text-teal-600 transition-colors">お問い合わせ</Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Breadcrumb */}
        <nav className="mb-8">
          <div className="flex items-center space-x-2 text-sm text-gray-500">
            <Link to="/" className="hover:text-teal-600">ホーム</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/beast-blog" className="hover:text-teal-600">ビースト君ブログ</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-gray-900">薬剤師が教える！災害時のお薬管理マニュアル</span>
          </div>
        </nav>

        {/* Article Header */}
        <article className="bg-white">
          <header className="mb-8">
            <div className="mb-4">
              <span className="inline-block bg-red-100 text-red-800 text-sm font-medium px-3 py-1 rounded-full">
                防災・緊急時対応
              </span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 leading-tight">
              薬剤師が教える！災害時のお薬管理マニュアル
            </h1>
            <div className="flex items-center space-x-4 text-gray-600 text-sm">
              <div className="flex items-center space-x-2">
                <i className="ri-calendar-line"></i>
                <span>2025年11月12日</span>
              </div>
              <div className="flex items-center space-x-2">
                <i className="ri-user-line"></i>
                <span>PharmaBeast薬剤師チーム</span>
              </div>
              <div className="flex items-center space-x-2">
                <i className="ri-time-line"></i>
                <span>読了時間: 約8分</span>
              </div>
            </div>
          </header>

          {/* Featured Image */}
          <div className="mb-8">
            <img 
              src="https://public.readdy.ai/ai/img_res/15cc825f97cac6b61a6625340789a56c.jpg"
              alt="災害時のお薬管理マニュアル - 防災薬セットと緊急時の薬剤管理"
              className="w-full h-96 object-cover rounded-lg shadow-lg"
            />
          </div>

          {/* Article Content */}
          <div className="prose prose-lg max-w-none">
            {/* Introduction */}
            <div className="bg-gradient-to-r from-red-50 to-orange-50 border-l-4 border-red-500 p-6 mb-8 rounded-r-lg">
              <h2 className="text-xl font-bold text-red-800 mb-3 flex items-center">
                <i className="ri-alert-line mr-2"></i>
                いざという時のために「防災×お薬」の備えを
              </h2>
              <p className="text-gray-700 leading-relaxed">
                地震や台風など、いつ起きるかわからない災害。そんな非常時に命を守るカギになるのが「薬の管理」です。
                普段は当たり前のように薬局に行ける環境も、災害時には途切れてしまうかもしれません。
                今回は薬剤師の視点から、「災害時のお薬の持ち出し・備え」についてお伝えします。
              </p>
            </div>

            {/* Section 1 */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <span className="bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm mr-3">1</span>
                災害時に困るのは"薬がないこと"
              </h2>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
                <p className="text-gray-700 leading-relaxed mb-4">
                  持病のある方はもちろん、花粉症や高血圧、糖尿病など、日常的に服薬している方にとって「薬が手に入らない」ことは大きなリスクです。
                </p>
                <p className="text-gray-700 leading-relaxed">
                  実際に、過去の災害では<strong className="text-red-600">"薬の名前が分からない"</strong>、<strong className="text-red-600">"お薬手帳を失くした"</strong>というケースが多く見られました。
                  だからこそ、平時からの準備がとても大切です。
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                  <h3 className="font-bold text-gray-900 mb-3 flex items-center">
                    <i className="ri-error-warning-line text-red-500 mr-2"></i>
                    よくある災害時の問題
                  </h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <i className="ri-close-circle-line text-red-500 mr-2 mt-1 flex-shrink-0"></i>
                      薬の名前や用量が分からない
                    </li>
                    <li className="flex items-start">
                      <i className="ri-close-circle-line text-red-500 mr-2 mt-1 flex-shrink-0"></i>
                      お薬手帳の紛失
                    </li>
                    <li className="flex items-start">
                      <i className="ri-close-circle-line text-red-500 mr-2 mt-1 flex-shrink-0"></i>
                      薬局の営業停止
                    </li>
                    <li className="flex items-start">
                      <i className="ri-close-circle-line text-red-500 mr-2 mt-1 flex-shrink-0"></i>
                      処方箋の再発行困難
                    </li>
                  </ul>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                  <h3 className="font-bold text-gray-900 mb-3 flex items-center">
                    <i className="ri-shield-check-line text-green-500 mr-2"></i>
                    事前準備の重要性
                  </h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <i className="ri-check-circle-line text-green-500 mr-2 mt-1 flex-shrink-0"></i>
                      命に関わる薬の確保
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-circle-line text-green-500 mr-2 mt-1 flex-shrink-0"></i>
                      症状悪化の防止
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-circle-line text-green-500 mr-2 mt-1 flex-shrink-0"></i>
                      避難所での適切な治療
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-circle-line text-green-500 mr-2 mt-1 flex-shrink-0"></i>
                      家族の安心確保
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Section 2 */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <span className="bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm mr-3">2</span>
                防災バッグに入れておきたい「お薬持ち物リスト」
              </h2>

              <div className="bg-gradient-to-r from-teal-50 to-blue-50 border border-teal-200 rounded-lg p-6 mb-6">
                <h3 className="text-lg font-bold text-teal-800 mb-3 flex items-center">
                  <i className="ri-medicine-bottle-line mr-2"></i>
                  薬剤師のおすすめ防災薬セット
                </h3>
                <p className="text-gray-700">以下のアイテムを防災バッグに常備しておきましょう 👇</p>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                {/* Basic Set */}
                <div className="bg-white border-2 border-teal-200 rounded-lg p-6">
                  <h3 className="text-xl font-bold text-teal-800 mb-4 flex items-center">
                    <i className="ri-first-aid-kit-line mr-2"></i>
                    基本セット
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <i className="ri-capsule-line text-teal-600 mr-3 mt-1 flex-shrink-0"></i>
                      <div>
                        <strong>現在服用中の薬</strong>
                        <p className="text-sm text-gray-600">最低3日分、できれば1週間分</p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-book-line text-teal-600 mr-3 mt-1 flex-shrink-0"></i>
                      <div>
                        <strong>お薬手帳または薬剤情報のコピー</strong>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-id-card-line text-teal-600 mr-3 mt-1 flex-shrink-0"></i>
                      <div>
                        <strong>保険証・医療証</strong>
                        <p className="text-sm text-gray-600">コピー可</p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-thermometer-line text-teal-600 mr-3 mt-1 flex-shrink-0"></i>
                      <div>
                        <strong>体温計・絆創膏・鎮痛薬・整腸薬・常備薬</strong>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-shield-line text-teal-600 mr-3 mt-1 flex-shrink-0"></i>
                      <div>
                        <strong>マスク・消毒用アルコール・ビニール袋</strong>
                      </div>
                    </li>
                  </ul>
                </div>

                {/* Additional Items */}
                <div className="bg-white border-2 border-blue-200 rounded-lg p-6">
                  <h3 className="text-xl font-bold text-blue-800 mb-4 flex items-center">
                    <i className="ri-add-circle-line mr-2"></i>
                    あると安心の追加アイテム
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <i className="ri-drop-line text-blue-600 mr-3 mt-1 flex-shrink-0"></i>
                      <div>
                        <strong>飲み水</strong>
                        <p className="text-sm text-gray-600">薬の服用にも使用</p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-cup-line text-blue-600 mr-3 mt-1 flex-shrink-0"></i>
                      <div>
                        <strong>清潔なコップまたは紙コップ</strong>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-pencil-line text-blue-600 mr-3 mt-1 flex-shrink-0"></i>
                      <div>
                        <strong>メモ帳とペン</strong>
                        <p className="text-sm text-gray-600">服薬記録や体調メモ用</p>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                <h4 className="font-bold text-yellow-800 mb-2 flex items-center">
                  <i className="ri-lightbulb-line mr-2"></i>
                  ポイント
                </h4>
                <p className="text-gray-700">
                  お薬は「防災バッグの中に小さなポーチ」でまとめておくと、持ち出しやすく管理もしやすいです。
                </p>
              </div>
            </section>

            {/* Section 3 */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <span className="bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm mr-3">3</span>
                薬剤師がすすめる"災害前の準備習慣"
              </h2>

              <div className="space-y-6">
                <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                  <h3 className="font-bold text-gray-900 mb-3 flex items-center">
                    <i className="ri-check-line text-green-500 mr-2"></i>
                    お薬手帳は常に最新に
                  </h3>
                  <p className="text-gray-700 mb-3">
                    薬局で更新してもらったら、スマホ撮影してクラウド保存も◎
                  </p>
                  <div className="bg-green-50 border border-green-200 rounded p-3">
                    <p className="text-sm text-green-800">
                      <strong>おすすめ：</strong> Google DriveやiCloudに「お薬手帳」フォルダを作成し、定期的に写真を保存
                    </p>
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                  <h3 className="font-bold text-gray-900 mb-3 flex items-center">
                    <i className="ri-check-line text-green-500 mr-2"></i>
                    残薬が少なくなったら早めに受診・処方を
                  </h3>
                  <p className="text-gray-700 mb-3">
                    災害時に途切れないよう、常に余裕を持つことが大切。
                  </p>
                  <div className="bg-blue-50 border border-blue-200 rounded p-3">
                    <p className="text-sm text-blue-800">
                      <strong>目安：</strong> 残り1週間分になったら次回の受診予約を取る
                    </p>
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                  <h3 className="font-bold text-gray-900 mb-3 flex items-center">
                    <i className="ri-check-line text-green-500 mr-2"></i>
                    家族の薬もリスト化
                  </h3>
                  <p className="text-gray-700 mb-3">
                    誰がどんな薬を飲んでいるか、1枚にまとめておくと救護所などで役立ちます。
                  </p>
                  <div className="bg-purple-50 border border-purple-200 rounded p-3">
                    <p className="text-sm text-purple-800">
                      <strong>記載項目：</strong> 氏名、年齢、薬品名、用法・用量、アレルギー情報
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 4 */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <span className="bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm mr-3">4</span>
                もし薬を失くしてしまったら
              </h2>

              <div className="bg-orange-50 border border-orange-200 rounded-lg p-6 mb-6">
                <h3 className="font-bold text-orange-800 mb-3 flex items-center">
                  <i className="ri-information-line mr-2"></i>
                  緊急時の対処法
                </h3>
                <p className="text-gray-700 mb-4">
                  避難所や薬局で「お薬手帳や薬の写真」を見せれば、薬剤師が内容を確認して再度用意できる場合があります。
                </p>
                <p className="text-gray-700">
                  手帳や処方情報がない場合でも、飲んでいた薬の<strong>名前・色・形・用途</strong>をできるだけ伝えましょう。
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                    <i className="ri-file-list-line text-blue-500 mr-2"></i>
                    伝えるべき情報
                  </h4>
                  <ul className="space-y-2 text-gray-700">
                    <li>• 薬の商品名または成分名</li>
                    <li>• 薬の色・形・大きさ</li>
                    <li>• 1日の服用回数と量</li>
                    <li>• 何の病気・症状のための薬か</li>
                    <li>• 処方してもらった病院名</li>
                  </ul>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                    <i className="ri-hospital-line text-green-500 mr-2"></i>
                    相談できる場所
                  </h4>
                  <ul className="space-y-2 text-gray-700">
                    <li>• 避難所の救護所</li>
                    <li>• 営業している薬局</li>
                    <li>• 災害時医療救護班</li>
                    <li>• 保健所・保健センター</li>
                    <li>• 災害派遣医療チーム（DMAT）</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Summary */}
            <section className="mb-10">
              <div className="bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-lg p-8">
                <h2 className="text-2xl font-bold mb-4 flex items-center">
                  <i className="ri-heart-pulse-line mr-3"></i>
                  まとめ：お薬の備えは"自分と家族を守る防災行動"
                </h2>
                <p className="text-lg leading-relaxed mb-6">
                  防災用品の中でも、お薬は「命を守る持ち物」のひとつ。
                  日々の薬を"防災視点"で見直すことで、災害時の不安をぐっと減らせます。
                </p>
                
                <div className="bg-white/10 rounded-lg p-6">
                  <h3 className="font-bold text-xl mb-4 flex items-center">
                    <i className="ri-foot-print-line mr-2"></i>
                    今日できることから始めましょう
                  </h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="flex items-center space-x-2">
                      <i className="ri-checkbox-circle-line text-green-300"></i>
                      <span>防災バッグの中身チェック</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <i className="ri-checkbox-circle-line text-green-300"></i>
                      <span>お薬手帳の更新</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <i className="ri-checkbox-circle-line text-green-300"></i>
                      <span>家族分の薬情報をまとめる</span>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Keywords */}
            <section className="mb-10">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                <h3 className="font-bold text-gray-900 mb-3 flex items-center">
                  <i className="ri-key-line mr-2"></i>
                  重要キーワード
                </h3>
                <p className="text-gray-700">
                  <strong className="text-red-600">「災害 薬 持ち出し」「防災 薬 持ち物」</strong>は、あなたの命を守るキーワードです。
                </p>
              </div>
            </section>
          </div>

          {/* Related Articles */}
          <section className="mt-12 pt-8 border-t border-gray-200">
            <h3 className="text-xl font-bold text-gray-900 mb-6">関連記事</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <Link to="/beast-blog/elderly-medication-management" className="group">
                <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Elderly%20person%20organizing%20medications%20with%20pill%20organizer%20and%20medication%20schedule%2C%20healthcare%20management%20for%20seniors%2C%20clean%20bright%20setting&width=300&height=200&seq=elderly-med-1&orientation=landscape"
                    alt="高齢者の服薬管理"
                    className="w-full h-32 object-cover rounded mb-3"
                  />
                  <h4 className="font-semibold text-gray-900 group-hover:text-teal-600 transition-colors">
                    高齢者の服薬管理のポイント
                  </h4>
                  <p className="text-sm text-gray-600 mt-2">
                    安全で確実な服薬のための管理方法をご紹介
                  </p>
                </div>
              </Link>
              <Link to="/beast-blog/family-pharmacist-guide" className="group">
                <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Family%20pharmacist%20consultation%20with%20family%20members%20discussing%20medication%20safety%2C%20professional%20healthcare%20setting%2C%20warm%20lighting&width=300&height=200&seq=family-pharm-1&orientation=landscape"
                    alt="かかりつけ薬剤師"
                    className="w-full h-32 object-cover rounded mb-3"
                  />
                  <h4 className="font-semibold text-gray-900 group-hover:text-teal-600 transition-colors">
                    かかりつけ薬剤師の選び方
                  </h4>
                  <p className="text-sm text-gray-600 mt-2">
                    信頼できる薬剤師との関係構築のコツ
                  </p>
                </div>
              </Link>
              <Link to="/beast-blog/drug-food-interactions" className="group">
                <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Medication%20and%20food%20interaction%20chart%20with%20pills%20and%20various%20foods%2C%20educational%20healthcare%20illustration%2C%20clean%20professional%20layout&width=300&height=200&seq=drug-food-1&orientation=landscape"
                    alt="薬と食べ物の相互作用"
                    className="w-full h-32 object-cover rounded mb-3"
                  />
                  <h4 className="font-semibold text-gray-900 group-hover:text-teal-600 transition-colors">
                    薬と食べ物の相互作用
                  </h4>
                  <p className="text-sm text-gray-600 mt-2">
                    注意すべき組み合わせと対処法
                  </p>
                </div>
              </Link>
            </div>
          </section>
        </article>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-8 h-8 bg-teal-600 rounded-lg flex items-center justify-center">
                  <i className="ri-capsule-fill text-white"></i>
                </div>
                <span className="text-xl font-bold" style={{ fontFamily: '"Pacifico", serif' }}>PharmaBeast</span>
              </div>
              <p className="text-gray-400 text-sm">
                薬剤師による信頼できる医療情報をお届けします
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">サービス</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><Link to="/services" className="hover:text-white transition-colors">サービス一覧</Link></li>
                <li><Link to="/family-pharmacist" className="hover:text-white transition-colors">かかりつけ薬剤師</Link></li>
                <li><Link to="/health-consultation" className="hover:text-white transition-colors">健康相談</Link></li>
                <li><Link to="/online-medication-guidance" className="hover:text-white transition-colors">オンライン服薬指導</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">情報</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><Link to="/beast-blog" className="hover:text-white transition-colors">ビースト君ブログ</Link></li>
                <li><Link to="/drug-price-search" className="hover:text-white transition-colors">薬価検索</Link></li>
                <li><Link to="/package-insert-search" className="hover:text-white transition-colors">添付文書検索</Link></li>
                <li><Link to="/emergency" className="hover:text-white transition-colors">緊急時対応</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">お問い合わせ</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><Link to="/contact" className="hover:text-white transition-colors">お問い合わせ</Link></li>
                <li><Link to="/privacy-policy" className="hover:text-white transition-colors">プライバシーポリシー</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-sm text-gray-400">
            <p>&copy; 2025 PharmaBeast. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-white transition-colors">Website Builder</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
